import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FieldArray, Formik } from 'formik';
import axios from 'axios';
import Moment from 'moment';
import {
  AppCard,
  AppDatePicker,
  AppInput,
  AppNoInfo,
  AppNotificationAlert,
  AppRemixIcon,
  AppSingleSelect,
} from "@/layout/AppComponents";
import ReactDOMServer from "react-dom/server";
import styled from "styled-components";
import { axiosPath } from "../../../../routing/helpers/router";
import Translator from "../../../../layout/components/helpers/Translator";
import BreadcrumbHandler from "../../../../layout/components/breadcrumb/BreadcrumbHandler";
import AppSubmitButton from "../../../../layout/components/form/AppSubmitButton";
import errorHandler from "@/layout/components/error/ErrorHandler";
import * as currentPageAction from "../../../../store/actions/currentPageAction";
import ModuleComsaEnvironmentalSideBarReports from "../ModuleComsaEnvironmentalSideBarReports";
import useIsMounted from "../../../../layout/components/hooks/useIsMounted";

export default function ModuleComsaEnvironmentalOfficeChangesManagementScreen() {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((state) => state.currentPage?.moduleData);
  const [initialValues, setInitialValues] = useState();
  const [noYesOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="no" />
      ),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="yes" />
      ),
      value: 1,
    },
  ]);

  // > LOAD DATA //
  const loadData = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_get"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            sheet: "changesManagement",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            setInitialValues(r.data?.sheetData ?? {});
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  useEffect(() => {
    loadData();
  }, []);
  // < LOAD DATA //

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    axios
      .post(axiosPath("api_r_user_module_comsaEnvironmental_getModuleData"), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change("moduleData", r.data));
        }
      });
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  // > SAVE FORM //
  const handleSave = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_edit"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            payload,
            sheet: "changesManagement",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change("moduleData", r.data?.moduleData, true)
            );
            AppNotificationAlert({
              message: ReactDOMServer.renderToString(
                <Translator file="module-comsaEnvironmental" name="completed" />
              ),
              type: "success",
              duration: 3000,
            });
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );
  // < SAVE FORM //

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${
                    moduleData.plantName
                  } - ${ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="evaluation"
                    />
                  )}`,
                  route: {
                    name: "user_module_legalCompliance_evaluation",
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="office"
                    />
                  ),
                },
              ]
            : [{ name: "..." }]
        }
      />
      <section className="content">
        <ModuleComsaEnvironmentalSideBarReports activeRoute="user_module_comsaEnvironmental_office_changes_management" />
        <div className="row">
          <div className="col-12">
            <Title>
              <Translator
                file="module-comsaEnvironmental"
                name="office.environmental.aspects.identification.evaluation"
              />
            </Title>
            <Subtitle className="mb-4">
              <Translator
                file="module-comsaEnvironmental"
                name="office.changes.management"
              />
            </Subtitle>
          </div>
        </div>
        {initialValues && (
          <Formik
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSave}
          >
            {({ setFieldValue, handleSubmit, values, isSubmitting }) => (
              <form onSubmit={handleSubmit}>
                <AppCard className="mb-2">
                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th className="col-sm-2">
                            <Translator
                              file="module-comsaEnvironmental"
                              name="revision.number"
                            />
                          </th>
                          <th className="col-sm-2">
                            <Translator
                              file="module-comsaEnvironmental"
                              name="date"
                            />
                          </th>
                          <th className="col-sm-3">
                            <Translator
                              file="module-comsaEnvironmental"
                              name="changes"
                            />
                          </th>
                          <th className="col-sm-3">
                            <Translator
                              file="module-comsaEnvironmental"
                              name="need.update"
                            />{" "}
                            <Translator
                              file="module-comsaEnvironmental"
                              name="yes.bar.no"
                            />
                          </th>
                          <th className="col-sm-2 text-center">
                            <Translator
                              file="module-comsaEnvironmental"
                              name="options"
                            />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <FieldArray
                          name="table"
                          render={(arrayHelpers) => (
                            <>
                              {values?.table &&
                                values?.table.map((t, i) => (
                                  <tr key={i}>
                                    <td>
                                      <AppInput
                                        onChange={(v) =>
                                          setFieldValue(
                                            `table[${i}].reviewNumber`,
                                            v
                                          )
                                        }
                                        value={values?.table[i]?.reviewNumber}
                                        isClearable
                                      />
                                    </td>
                                    <td>
                                      <AppDatePicker
                                        title={ReactDOMServer.renderToString(
                                          <Translator
                                            file="module-comsaEnvironmental"
                                            name="release.date"
                                          />
                                        )}
                                        value={
                                          values?.table[i]?.date
                                            ? Moment.unix(
                                                values?.table[i]?.date
                                              ).format("DD/MM/YYYY")
                                            : ""
                                        }
                                        onChange={(date) =>
                                          setFieldValue(
                                            `table[${i}].date`,
                                            date
                                              ? Moment(
                                                  date,
                                                  "DD/MM/YYYY"
                                                ).unix()
                                              : ""
                                          )
                                        }
                                        placeholder="dd/mm/yyyy"
                                        isClearable
                                        showMonthDropdown
                                        showYearDropdown
                                      />
                                    </td>
                                    <td>
                                      <AppInput
                                        onChange={(v) =>
                                          setFieldValue(
                                            `table[${i}].changes`,
                                            v
                                          )
                                        }
                                        value={values?.table[i]?.changes}
                                        isClearable
                                      />
                                    </td>
                                    <td>
                                      <AppSingleSelect
                                        options={noYesOptions}
                                        value={values?.table[i]?.needUpdate}
                                        onChange={(opt) =>
                                          setFieldValue(
                                            `table[${i}].needUpdate`,
                                            opt
                                          )
                                        }
                                        isClearable
                                      />
                                    </td>
                                    <td className="text-center align-middle">
                                      <span
                                        className="btn btn-danger btn-xs"
                                        onClick={() =>
                                          setFieldValue(
                                            "table",
                                            values?.table.filter(
                                              (n, index) => index !== i
                                            )
                                          )
                                        }
                                        title={ReactDOMServer.renderToString(
                                          <Translator
                                            file="module-comsaEnvironmental"
                                            name="delete"
                                          />
                                        )}
                                      >
                                        <AppRemixIcon icon="delete-bin-6" />
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              <tr>
                                <td colSpan={4} />
                                <td className="text-center align-middle">
                                  <span
                                    className="btn btn-success btn-xs"
                                    onClick={() =>
                                      arrayHelpers.push({
                                        reviewNumber: "",
                                        date: null,
                                        changes: "",
                                        needUpdate: null,
                                      })
                                    }
                                  >
                                    <Translator
                                      file="module-comsaEnvironmental"
                                      name="add"
                                    />
                                  </span>
                                </td>
                              </tr>
                            </>
                          )}
                        />
                      </tbody>
                    </table>
                  </div>
                </AppCard>
                <SubmitWrapper>
                  <AppSubmitButton
                    className="btn btn-sm btn-success"
                    isSubmitting={isSubmitting}
                  >
                    <Translator file="module-comsaEnvironmental" name="save" />
                  </AppSubmitButton>
                </SubmitWrapper>
              </form>
            )}
          </Formik>
        )}
      </section>
    </div>
  );
}

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;

const Subtitle = styled.h4`
  text-align: center;
`;

const SubmitWrapper = styled.div`
  position: fixed;
  right: 15px;
  bottom: 8px;
  z-index: 10;
`;
